import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import type { ListStatuses, Status, StatusCode } from "~/types/api";

const buildChosenStatuses = (
  codes: number[],
  workflowStatuses: Status[],
  sortOrderLookup: Map<number, number>
): ListStatuses[] => {
  const maxExisting =
    sortOrderLookup.size > 0 ? Math.max(...Array.from(sortOrderLookup.values())) : -1;
  let nextOrder = maxExisting + 1;

  return codes
    .map((code) => {
      const full = workflowStatuses.find((s) => s.code === code);
      if (!full) return null;
      const sortOrder = sortOrderLookup.get(code) ?? nextOrder++;
      return { code: full.code, name: full.name, sortOrder };
    })
    .filter((s): s is ListStatuses => s !== null)
    .sort((a, b) => a.sortOrder - b.sortOrder);
};

export const useStatusSelection = (
  mode: "create" | "edit",
  workflowListStatuses: StatusCode[] | undefined,
  workflowStatuses: Status[] | undefined
) => {
  const originalStatusCodes = useMemo(
    () => (workflowListStatuses ?? []).map((s) => s.code),
    [workflowListStatuses]
  );

  const [chosenStatuses, setChosenStatuses] = useState<ListStatuses[]>([]);
  const [selectedAvailableItems, setSelectedAvailableItems] = useState<number[]>([]);
  const [selectedSelectedItems, setSelectedSelectedItems] = useState<number[]>([]);

  const prevIdentityRef = useRef<string | null>(null);

  const identityKey = useMemo(() => {
    const entries = (workflowListStatuses ?? []).map((s) => `${s.code}:${s.sortOrder}`).join(",");
    return `${entries}-${workflowStatuses?.length ?? 0}`;
  }, [workflowListStatuses, workflowStatuses]);

  useEffect(() => {
    if (!workflowStatuses) {
      setChosenStatuses([]);
      return;
    }

    const isNewIdentity = prevIdentityRef.current !== identityKey;

    if (!isNewIdentity && chosenStatuses.length > 0) {
      return;
    }

    prevIdentityRef.current = identityKey;

    const sortOrderLookup = new Map<number, number>(
      (workflowListStatuses ?? []).map((s) => [s.code, s.sortOrder])
    );
    const codes = (workflowListStatuses ?? []).map((s) => s.code);
    setChosenStatuses(buildChosenStatuses(codes, workflowStatuses, sortOrderLookup));
  }, [identityKey, workflowListStatuses, workflowStatuses]);

  const selectedCodes = useMemo(() => chosenStatuses.map((s) => s.code), [chosenStatuses]);

  const setSelectedCodes = useCallback(
    (codesOrUpdater: number[] | ((prev: number[]) => number[])) => {
      setChosenStatuses((prev) => {
        if (!workflowStatuses) return prev;
        const newCodes =
          typeof codesOrUpdater === "function"
            ? codesOrUpdater(prev.map((s) => s.code))
            : codesOrUpdater;
        const sortOrderLookup = new Map<number, number>(prev.map((s) => [s.code, s.sortOrder]));
        return buildChosenStatuses(newCodes, workflowStatuses, sortOrderLookup);
      });
    },
    [workflowStatuses]
  );

  const availableStatuses = useMemo<ListStatuses[]>(
    () =>
      (workflowStatuses ?? [])
        .filter((s) => !selectedCodes.includes(s.code))
        .map((s, i) => ({ code: s.code, name: s.name, sortOrder: i })),
    [workflowStatuses, selectedCodes]
  );

  const hasStatusChanges = useMemo(() => {
    if (mode === "create") return chosenStatuses.length > 0;
    return (
      chosenStatuses.length !== originalStatusCodes.length ||
      chosenStatuses.some((s) => !originalStatusCodes.includes(s.code)) ||
      originalStatusCodes.some((code) => !chosenStatuses.some((s) => s.code === code)) ||
      chosenStatuses.some((s) => {
        const orig = (workflowListStatuses ?? []).find((o) => o.code === s.code);
        return orig ? orig.sortOrder !== s.sortOrder : true;
      })
    );
  }, [mode, chosenStatuses, originalStatusCodes, workflowListStatuses]);

  const resetStatuses = useCallback(() => {
    setChosenStatuses([]);
    setSelectedAvailableItems([]);
    setSelectedSelectedItems([]);
  }, []);

  const reorderChosenStatuses = useCallback((reordered: ListStatuses[]) => {
    setChosenStatuses(reordered);
  }, []);

  return {
    selectedCodes,
    setSelectedCodes,
    originalStatusCodes,
    availableStatuses,
    chosenStatuses,
    hasStatusChanges,
    resetStatuses,
    reorderChosenStatuses,
    selectedAvailableItems,
    setSelectedAvailableItems,
    selectedSelectedItems,
    setSelectedSelectedItems,
  };
};
